import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, DatePicker, Select } from 'antd';
import { chunk } from 'lodash';
import { commissionLevel as CommissionLevelConst } from '~constants';

const CreateForm = ({ t, form, landlord, commissionLevel, modalType, currency = 'USD' }) => {
  const { getFieldDecorator, getFieldsValue, validateFields, setFieldsValue } = form;
  const [allInputsEmpty, setAllInputsEmpty] = useState(false);

  const handleBlur = () => {
    const values = getFieldsValue();
    const updatedValues = { ...values };

    Object.keys(values.currencyValues).forEach(key => {
      const value = values.currencyValues[key];
      if (value && /^\d+\.$/.test(value)) {
        updatedValues.currencyValues[key] = `${value}00`;
      }
    });

    setFieldsValue(updatedValues);

    const allEmpty = Object.values(updatedValues.currencyValues).every(value => !value);
    setAllInputsEmpty(allEmpty);

    if (!allEmpty) {
      validateFields(['currencyValues'], { force: true });
    }
  };

  return (
    <div className="create-form">
      <div className="create-form__details">
        <h3 className="create-form__title">Details</h3>

        <Form.Item label={t('cms.commission.create.name', 'Commission Name:')}>
          {getFieldDecorator('name', {
            rules: [
              {
                required: true,
                message: t('cms.commission.create.name.required', 'Please input commission name'),
              },
            ],
          })(
            <Input
              placeholder={t('cms.commission.create.name.placeholder', 'Input commission name')}
            />,
          )}
        </Form.Item>

        <div className="create-form__row">
          <Form.Item label={t('cms.commission.create.category', 'Commission Category:')}>
            {getFieldDecorator('category', {
              initialValue: 'FLAT_FEE',
              rules: [
                {
                  required: true,
                  message: t('cms.property.commission.not_be_empty.err'),
                },
              ],
            })(
              <Select
                disabled={true}
                style={{ width: '100%' }}
                placeholder={t('cms.property.commission_form_modal.commission_type.placeholder')}
              >
                <For of={['FLAT_FEE']} each="type">
                  <Select.Option style={{ width: '100%' }} key={type} value={type}>
                    {t(
                      `cms.property.commission_form_modal.commission_category.select.${type.toLowerCase()}`,
                    )}
                  </Select.Option>
                </For>
              </Select>,
            )}
          </Form.Item>

          <Choose>
            <When condition={commissionLevel === CommissionLevelConst.LANDLORD}>
              <Form.Item
                label={t('cms.commission.create.listing_plan', 'Listing Plan:')}
                className="create-form__value"
              >
                {getFieldDecorator('bookingJourney', {
                  rules: [
                    {
                      message: t('cms.commission.create.value.required', 'Please select value'),
                    },
                  ],
                })(
                  <Select
                    style={{ width: '100%' }}
                    disabled={modalType === 'edit'}
                    placeholder={t(
                      'cms.property.commission_form_modal.commission_type.placeholder',
                    )}
                  >
                    <For
                      of={[
                        { label: 'Book', value: 'SEMI_AUTOMATIC' },
                        { label: 'Connect(Student Suite)', value: 'FULL_AUTOMATIC' },
                        { label: 'Connect(Student.Com)', value: 'INSTANT_BOOK' },
                        { label: 'List', value: 'LEAD_GENERATION' },
                      ]}
                      each="type"
                    >
                      <Select.Option style={{ width: '100%' }} key={type.label} value={type.value}>
                        {type.label}
                      </Select.Option>
                    </For>
                  </Select>,
                )}
              </Form.Item>
            </When>
            <Otherwise>
              <Form.Item
                label={t('cms.commission.create.value', 'Commission Value:')}
                className="create-form__value"
              >
                {getFieldDecorator('value', {
                  rules: [
                    {
                      required: true,
                      message: t('cms.commission.create.value.required', 'Please input value'),
                    },
                  ],
                })(<Input placeholder="e.g. 8.00" addonAfter={currency} />)}
              </Form.Item>
            </Otherwise>
          </Choose>
        </div>

        <div className="create-form__row">
          <Form.Item label={t('cms.commission.create.effective_from', 'Effective from:')}>
            {getFieldDecorator('effectiveFrom', {
              rules: [
                {
                  required: true,
                  message: t('cms.commission.create.effective_from.required', 'Please select date'),
                },
              ],
            })(
              <DatePicker
                placeholder={t('cms.commission.create.effective_from.placeholder', 'Select date')}
              />,
            )}
          </Form.Item>

          <Form.Item label={t('cms.commission.create.effective_to', 'Effective to (optional):')}>
            {getFieldDecorator('effectiveTo')(
              <DatePicker
                placeholder={t('cms.commission.create.effective_to.placeholder', 'Select date')}
              />,
            )}
          </Form.Item>
        </div>

        <If condition={commissionLevel === CommissionLevelConst.LANDLORD}>
          <h3 className="create-form__title">Commission Value</h3>
          {allInputsEmpty && (
            <div className="create-form__values--hasError">
              {t('cms.commission.currency.empty_err', 'Please input at least one currency value')}
            </div>
          )}
          <div className="desc" style={{ marginBottom: '16px' }}>
            The system will check the currency based on the countries where all your properties are
            located
          </div>
          <div className="create-form__values-grid">
            {chunk(landlord?.propertyCurrencies || [], 2).map((row, rowIndex) => (
              <div key={rowIndex} className="create-form__values-row">
                {row.map(currencyCode => (
                  <Form.Item key={currencyCode} className="create-form__value-item">
                    {getFieldDecorator(`currencyValues.${currencyCode}`, {
                      rules: [
                        {
                          validator: (rule, value, callback) => {
                            const naturalNumberRegExp = /^(0|[1-9]\d*)(\.\d*)?$/;
                            if (value && !naturalNumberRegExp.test(value)) {
                              setFieldsValue({ [rule.field]: '' });
                            }
                            const values = getFieldsValue();
                            const allEmpty = Object.values(values.currencyValues).every(
                              value => !value,
                            );
                            setAllInputsEmpty(allEmpty);
                            if (!allEmpty) {
                              callback();
                            } else {
                              callback('');
                            }
                          },
                        },
                      ],
                    })(
                      <Input
                        placeholder={`${currencyCode} e.g. 8.00`}
                        addonAfter={currencyCode}
                        onBlur={handleBlur}
                      />,
                    )}
                  </Form.Item>
                ))}
              </div>
            ))}
          </div>
        </If>
      </div>
    </div>
  );
};

CreateForm.propTypes = {
  t: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  currency: PropTypes.string,
};

export default CreateForm;
